/**
 * Calculate tokenomics based on current presale status
 * @param {Object} tokenomicsConfig - Tokenomics configuration from JSON
 * @param {number} ethPrice - Current ETH price in USD
 * @param {number} totalContributedEth - Total ETH contributed
 * @param {number} uniqueContributors - Number of unique contributors
 * @returns {Object} Calculated tokenomics
 */
export const calculateTokenomics = (tokenomicsConfig, ethPrice, totalContributedEth, uniqueContributors) => {
    if (!tokenomicsConfig || !ethPrice || totalContributedEth === undefined) {
      return null;
    }
  
    const { totalSupply, distribution } = tokenomicsConfig;
    const totalValueRaised = totalContributedEth * ethPrice;
    
    // Calculate token distribution amounts
    const distributionAmounts = {};
    Object.keys(distribution).forEach(key => {
      const tokensForCategory = (totalSupply * distribution[key].percentage) / 100;
      distributionAmounts[key] = {
        ...distribution[key],
        tokens: tokensForCategory,
        valueUsd: key === 'presale' ? totalValueRaised : 0
      };
    });
    
    // Calculate tokens per contributor (equally distributed among contributors)
    const tokensPerContributor = uniqueContributors > 0
      ? distributionAmounts.presale.tokens / uniqueContributors
      : 0;
    
    // Calculate tokens per ETH - total presale tokens divided by total ETH allocation
    const tokensPerEth = totalContributedEth > 0
      ? distributionAmounts.presale.tokens / totalContributedEth
      : 0;
    
    // Calculate initial token price in USD based on presale metrics
    const initialTokenPriceUsd = totalContributedEth > 0
      ? Number(((totalContributedEth * ethPrice) / distributionAmounts.presale.tokens).toFixed(6))
      : 0;
    
    // Calculate liquidity pool values
    const liquidityPoolEthValue = totalContributedEth;
    const liquidityPoolTokens = distributionAmounts.liquidity.tokens;
    // LP value includes both ETH and token value
    const liquidityPoolUsdValue = liquidityPoolEthValue * ethPrice * 2;
    
    // Calculate market metrics
    const initialMarketCap = initialTokenPriceUsd * totalSupply;
    const fullyDilutedValuation = totalSupply * initialTokenPriceUsd;
    
    // Return comprehensive tokenomics data
    return {
      totalSupply,
      totalContributedEth,
      totalValueRaised,
      ethPrice,
      distribution: distributionAmounts,
      metrics: {
        tokensPerContributor,
        tokensPerEth,
        initialTokenPriceUsd,
        liquidityPoolEthValue,
        liquidityPoolTokens,
        liquidityPoolUsdValue,
        initialMarketCap,
        fullyDilutedValuation,
        uniqueContributors
      }
    };
};

/**
 * Calculate token allocation for a specific contributor
 * @param {number} contributionAmount - Amount contributed in ETH
 * @param {Object} tokenomics - Calculated tokenomics
 * @returns {Object} Contributor-specific calculations
 */
export const calculateContributorAllocation = (contributionAmount, tokenomics) => {
    if (!tokenomics || !contributionAmount) {
      return null;
    }
    
    const { tokensPerEth, initialTokenPriceUsd } = tokenomics.metrics;
    
    const tokensAllocated = contributionAmount * tokensPerEth;
    const valueInUsd = contributionAmount * tokenomics.ethPrice;
    const percentageOfPresale = tokenomics.totalContributedEth > 0
      ? (contributionAmount / tokenomics.totalContributedEth) * 100
      : 0;
    
    return {
      contributionEth: contributionAmount,
      valueUsd: valueInUsd,
      tokensAllocated,
      percentageOfPresale,
      tokenValueAtLaunch: initialTokenPriceUsd * tokensAllocated
    };
};

/**
 * Check if a contribution is valid
 * @param {number} amount - Contribution amount in ETH
 * @param {number} minContribution - Minimum allowed contribution
 * @param {number} maxContribution - Maximum allowed contribution
 * @returns {boolean} Is the contribution valid
 */
export const isValidContribution = (amount, minContribution, maxContribution) => {
    return amount >= minContribution && amount <= maxContribution;
};

/**
 * Calculate ratios for pie chart visualization
 * @param {Object} tokenomics - Calculated tokenomics
 * @returns {Array} Data formatted for charts
 */
export const prepareChartData = (tokenomics) => {
    if (!tokenomics) return [];
    
    return Object.keys(tokenomics.distribution).map(key => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      value: tokenomics.distribution[key].tokens,
      percentage: tokenomics.distribution[key].percentage,
      description: tokenomics.distribution[key].description,
      usdValue: tokenomics.metrics.initialTokenPriceUsd * tokenomics.distribution[key].tokens
    }));
};

/**
 * Format large numbers for display
 * @param {number} num - Number to format
 * @returns {string} Formatted number with appropriate suffix
 */
export const formatLargeNumber = (num) => {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(2) + 'B';
    }
    if (num >= 1000000) {
        return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(2) + 'K';
    }
    return num.toFixed(2);
};